import React, { useEffect, useMemo, useRef, useState } from 'react';
import LearningHeader from './LearningHeader';
import LearningSidebar from './LearningSidebar';
import LearningFooterNav from './LearningFooterNav';
import { useMoodleUi } from '../Moodle/MoodleUiContext';
import { useMoodleNavigation } from '../Moodle/MoodleNavigationContext';
import { useMoodleLearningModel } from '../Moodle/Learning/useMoodleLearningModel';
import MoodleLearningRenderer from '../Moodle/Learning/MoodleLearningRenderer';
import { computeRequiredProgressFromSections } from '../Moodle/Learning/MoodleLearningProgress';
import { useMoodleViewPage } from '../../../hooks/useMoodleHooks';


function getStorageKey(provider: string, instance: string, courseId: string) {
  return `learning:lastViewed:${provider}:${instance}:${courseId}`;
}

function getCompletionStorageKey(provider: string, instance: string, courseId: string) {
  return `learning:localCompletion:${provider}:${instance}:${courseId}`;
}

interface LearningContentPageProps {
  provider: 'moodle';
  courseId: string;
  itemId?: string;
  onBack?: () => void;
  instance?: 'external' | 'internal';
  fullscreen?: boolean;
  returnTo?: string;
  onExit?: () => void;
  backLabel?: string;
}

export default function LearningContentPage({
                                              provider,
                                              courseId,
                                              itemId,
                                              onBack,
                                              instance: explicitInstance,
                                              fullscreen = false,
                                              returnTo,
                                              onExit,
                                              backLabel,
                                            }: LearningContentPageProps) {
  const { config } = useMoodleUi();
  const nav = useMoodleNavigation();

  const instance = explicitInstance ?? config.instance;
  const { model, isLoading, error } = useMoodleLearningModel(courseId, instance);

  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(
    itemId ? String(itemId) : undefined
  );

  const [locallyCompletedIds, setLocallyCompletedIds] = useState<Set<string>>(new Set());

  /**
   * Prevent duplicate remote page-view calls from rerenders/effect re-entry.
   */
  const trackedRemoteViewsRef = useRef<Set<string>>(new Set());

  /**
   * Page-specific Moodle view endpoint:
   * POST /api/moodle/page/<pageId>/view/
   * or /api/moodle/cyber-academy/page/<pageId>/view/
   */
  const moodleViewPage = useMoodleViewPage(undefined, instance);

  useEffect(() => {
    const raw = localStorage.getItem(getCompletionStorageKey(provider, instance, String(courseId)));
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      const ids = Array.isArray(parsed?.itemIds) ? parsed.itemIds.map(String) : [];
      setLocallyCompletedIds(new Set(ids));
    } catch {
      // ignore malformed storage
    }
  }, [provider, instance, courseId]);

  const modelWithLocalCompletion = useMemo(() => {
    const sections = model.sections.map((section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        isComplete: item.isComplete || locallyCompletedIds.has(String(item.id)),
      })),
    }));

    const helperSections = sections.map((section) => ({
      id: section.id,
      modules: section.items.map((it) => ({
        id: it.id,
        visible: it.visible !== false,
        uservisible: it.visible !== false,
        modname: it.type === 'label' ? 'label' : 'unknown',
        completiondata: {
          hascompletion: !!it.isRequired,
          isoverallcomplete: !!it.isComplete,
        },
      })),
    }));

    const progress = computeRequiredProgressFromSections(helperSections, locallyCompletedIds);
    const items = sections.flatMap((section) => section.items);

    return {
      ...model,
      sections,
      items,
      progress: {
        completed: progress.completed,
        total: progress.total,
        percent: progress.percent,
      },
    };
  }, [model, locallyCompletedIds]);

  useEffect(() => {
    if (itemId) {
      setSelectedItemId(String(itemId));
      return;
    }

    if (!modelWithLocalCompletion.items.length) return;

    const raw = localStorage.getItem(getStorageKey(provider, instance, String(courseId)));
    if (!raw) {
      setSelectedItemId(modelWithLocalCompletion.items[0]?.id);
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const restoredId = parsed?.itemId ? String(parsed.itemId) : undefined;
      if (restoredId && modelWithLocalCompletion.items.some((i) => i.id === restoredId)) {
        setSelectedItemId(restoredId);
        return;
      }
    } catch {
      // ignore malformed localStorage
    }

    setSelectedItemId(modelWithLocalCompletion.items[0]?.id);
  }, [itemId, modelWithLocalCompletion.items, provider, instance, courseId]);

  const selectedItem = useMemo(() => {
    if (!modelWithLocalCompletion.items.length) return undefined;

    if (selectedItemId) {
      return (
        modelWithLocalCompletion.items.find((i) => i.id === String(selectedItemId)) ||
        modelWithLocalCompletion.items[0]
      );
    }

    return modelWithLocalCompletion.items[0];
  }, [modelWithLocalCompletion.items, selectedItemId]);

  /**
   * Local fallback persistence/progress.
   * This remains provider-aware and is safe for future non-Moodle providers.
   */
  useEffect(() => {
    if (!selectedItem?.id) return;

    localStorage.setItem(
      getStorageKey(provider, instance, String(courseId)),
      JSON.stringify({
        itemId: selectedItem.id,
        updatedAt: Date.now(),
      })
    );

    setLocallyCompletedIds((prev) => {
      if (prev.has(String(selectedItem.id))) return prev;

      const next = new Set(prev);
      next.add(String(selectedItem.id));

      localStorage.setItem(
        getCompletionStorageKey(provider, instance, String(courseId)),
        JSON.stringify({
          itemIds: Array.from(next),
          updatedAt: Date.now(),
        })
      );

      window.dispatchEvent(new Event('learning-completion-updated'));
      return next;
    });
  }, [selectedItem?.id, provider, instance, courseId]);

  /**
   * Remote Moodle tracking:
   * currently only page resources have a confirmed backend "view" endpoint.
   *
   * For page items:
   * - selectedItem.raw.id is typically the course module id
   * - selectedItem.raw.instance is the Moodle page id
   * The backend endpoint expects the page id.
   */
  useEffect(() => {
    if (provider !== 'moodle') return;
    if (!selectedItem?.id || !selectedItem?.raw) return;
    if (selectedItem.type !== 'page') return;

    const raw = selectedItem.raw as any;
    const pageId = Number(raw?.instance);

    if (!pageId || Number.isNaN(pageId)) return;

    const trackingKey = `${provider}:${instance}:${courseId}:page:${pageId}`;
    if (trackedRemoteViewsRef.current.has(trackingKey)) return;

    trackedRemoteViewsRef.current.add(trackingKey);

    moodleViewPage.mutate(
      { pageId },
      {
        onError: () => {
          trackedRemoteViewsRef.current.delete(trackingKey);
        },
      }
    );
  }, [
    provider,
    instance,
    courseId,
    selectedItem?.id,
    selectedItem?.type,
    selectedItem?.raw,
    moodleViewPage,
  ]);

  const selectedIndex = useMemo(() => {
    if (!selectedItem) return -1;
    return modelWithLocalCompletion.items.findIndex((i) => i.id === selectedItem.id);
  }, [modelWithLocalCompletion.items, selectedItem]);

  const prevItem =
    selectedIndex > 0 ? modelWithLocalCompletion.items[selectedIndex - 1] : undefined;

  const nextItem =
    selectedIndex >= 0 && selectedIndex < modelWithLocalCompletion.items.length - 1
      ? modelWithLocalCompletion.items[selectedIndex + 1]
      : undefined;

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (onExit) {
      onExit();
      return;
    }

    nav.openCourse(Number(courseId), 'course-content');
  };

  const handleSelectItem = (selectedId: string) => {
    setSelectedItemId(String(selectedId));
    nav.openLearningItem(Number(courseId), Number(selectedId));
  };

  const handlePrev = () => {
    if (!prevItem) return;

    setSelectedItemId(String(prevItem.id));
    nav.openLearningItem(Number(courseId), Number(prevItem.id));
  };

  const handleNext = () => {
    if (!nextItem) return;

    setSelectedItemId(String(nextItem.id));
    nav.openLearningItem(Number(courseId), Number(nextItem.id));
  };

  if (isLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading learning content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Failed to load learning content.</p>
      </div>
    );
  }

  if (!modelWithLocalCompletion.items.length) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No learning items found.</p>
      </div>
    );
  }

  return (
    <div className={`learning-page-shell ${fullscreen ? 'learning-page-shell--fullscreen' : ''}`}>
      <LearningHeader
        courseTitle={modelWithLocalCompletion.title}
        currentTitle={selectedItem?.title}
        progressPercent={modelWithLocalCompletion.progress.percent}
        progressLabel={`${modelWithLocalCompletion.progress.completed} of ${modelWithLocalCompletion.progress.total} complete`}
        onBack={handleBack}
        backLabel={backLabel ?? (fullscreen ? 'Exit back to toolbox' : undefined)}
      />

      <div className="learning-page-body">
        <LearningSidebar
          sections={modelWithLocalCompletion.sections}
          selectedItemId={selectedItem?.id}
          onSelectItem={handleSelectItem}
        />

        <main className="learning-page-content">
          {provider === 'moodle' && selectedItem && (
            <MoodleLearningRenderer
              courseId={courseId}
              item={selectedItem}
              instance={instance}
            />
          )}
        </main>
      </div>

      <LearningFooterNav
        prevLabel={prevItem ? prevItem.title : 'Previous'}
        nextLabel={nextItem ? nextItem.title : 'Next'}
        onPrev={handlePrev}
        onNext={handleNext}
        disablePrev={!prevItem}
        disableNext={!nextItem}
      />

      <style>{`
        .learning-page-shell {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          background: #f8fafc;
        }

        .learning-page-shell--fullscreen {
          position: relative;
          width: 100%;
          height: 100%;
          min-height: 100%;
          background: #f8fafc;
        }

        .learning-page-body {
          display: flex;
          flex: 1 1 auto;
          min-height: 0;
        }

        .learning-page-content {
          flex: 1 1 auto;
          min-width: 0;
          overflow: auto;
          background: #f8fafc;
        }
      `}</style>
    </div>
  );
}